import React, { useEffect, useState } from 'react';
import axios from 'axios';

const PlatformAdminShopView = () => {
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [shopId, setShopId] = useState('');
  const [productCount, setProductCount] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [orders, setOrders] = useState([]);
  const [claims, setClaims] = useState([]);

  const params = new URLSearchParams(window.location.search);
  const currentShopId = params.get('id') || '';

  useEffect(() => {
    fetchData();
  }, [window.location.search]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `/api/platform_admin/shop_view?id=${encodeURIComponent(currentShopId)}`,
        { withCredentials: true }
      );

      if (res.data && res.data.ok) {
        setShop(res.data.shop || null);
        setShopId(String(res.data.shop_id || ''));
        setProductCount(Number(res.data.product_count || 0));
        setOrderCount(Number(res.data.order_count || 0));
        setTotalSales(Number(res.data.total_sales || 0));
        setOrders(Array.isArray(res.data.orders) ? res.data.orders : []);
        setClaims(Array.isArray(res.data.claims) ? res.data.claims : []);
      } else if (res.data && res.data.redirect) {
        window.location.href = res.data.redirect;
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        const redirectUrl =
          err.response.data && err.response.data.redirect
            ? err.response.data.redirect
            : '/platform_admin/login';
        window.location.href = redirectUrl;
        return;
      }

      if (err.response && err.response.data && err.response.data.msg) {
        alert(err.response.data.msg);
        if (err.response.status === 400 || err.response.status === 404) {
          window.location.href = '/platform_admin/shops';
          return;
        }
      }

      console.error('상점 상세 로드 실패:', err);
      setShop(null);
      setOrders([]);
      setClaims([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (e) => {
    e.preventDefault();

    try {
      const res = await axios.get('/api/platform_admin/logout', {
        withCredentials: true,
      });

      if (res.data && res.data.ok && res.data.redirect) {
        window.location.href = res.data.redirect;
        return;
      }

      window.location.href = '/platform_admin/login';
    } catch (err) {
      console.error('로그아웃 실패:', err);
      window.location.href = '/platform_admin/login';
    }
  };

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString('ko-KR');
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return String(value).replace('T', ' ').slice(0, 19);
  };

  const claimTypeText = (type) => {
    return type === 'exchange' ? '교환' : '반품';
  };

  return (
    <div className="bg-light min-vh-100">
      <nav className="navbar navbar-dark bg-dark px-4">
        <span className="navbar-brand">상점 상세</span>
        <div>
          <a href="/platform_admin/shops" className="btn btn-sm btn-outline-light me-2">
            상점목록
          </a>
          <a href={`/platform_admin/orders?shop_id=${shopId}`} className="btn btn-sm btn-outline-light me-2">
            주문
          </a>
          <a href={`/platform_admin/claims?shop_id=${shopId}`} className="btn btn-sm btn-outline-light me-2">
            클레임
          </a>
          <a href="/platform_admin/logout" onClick={handleLogout} className="btn btn-sm btn-danger">
            로그아웃
          </a>
        </div>
      </nav>

      <div className="container my-4">
        {loading ? (
          <div className="text-center py-5 text-muted">불러오는 중...</div>
        ) : !shop ? (
          <div className="text-center py-5 text-muted">상점이 존재하지 않습니다.</div>
        ) : (
          <>
            <h4 className="mb-3">
              🏪 {shop.name}{' '}
              {shop.status !== 'active' ? (
                <span className="badge bg-secondary">정지</span>
              ) : null}
            </h4>

            <p className="text-muted mb-4">
              소유자: {shop.owner_id} | 생성일: {formatDateTime(shop.created_at)}
            </p>

            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card shadow-sm text-center">
                  <div className="card-body">
                    <div className="text-muted">상품 수</div>
                    <h3>{formatNumber(productCount)}</h3>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card shadow-sm text-center">
                  <div className="card-body">
                    <div className="text-muted">주문 수</div>
                    <h3>{formatNumber(orderCount)}</h3>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card shadow-sm text-center">
                  <div className="card-body">
                    <div className="text-muted">총 매출</div>
                    <h3>{formatNumber(totalSales)}원</h3>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-7">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">최근 주문</div>
                  <div className="card-body p-0">
                    <table className="table mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>주문자</th>
                          <th>금액</th>
                          <th>상태</th>
                          <th>일시</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center py-3">
                              주문 없음
                            </td>
                          </tr>
                        ) : (
                          orders.map((o) => (
                            <tr key={o.id}>
                              <td>
                                <a href={`/platform_admin/order_view?id=${o.id}`}>
                                  {o.id}
                                </a>
                              </td>
                              <td>{o.buyer_name}</td>
                              <td>{formatNumber(o.result_price)}원</td>
                              <td>{o.status}</td>
                              <td>{formatDateTime(o.order_date)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-md-5">
                <div className="card shadow-sm h-100">
                  <div className="card-header bg-white fw-bold">최근 클레임</div>
                  <div className="card-body p-0">
                    <table className="table mb-0">
                      <thead>
                        <tr>
                          <th>유형</th>
                          <th>회원</th>
                          <th>상태</th>
                          <th>일시</th>
                        </tr>
                      </thead>
                      <tbody>
                        {claims.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center py-3">
                              클레임 없음
                            </td>
                          </tr>
                        ) : (
                          claims.map((c) => (
                            <tr key={c.id}>
                              <td>{claimTypeText(c.type)}</td>
                              <td>{c.nickname || '탈퇴회원'}</td>
                              <td>{c.status}</td>
                              <td>{formatDateTime(c.created_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlatformAdminShopView;